import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Cookie, X } from 'lucide-react';
import { PrivacyPolicy } from '@/components/PrivacyPolicy';

const COOKIE_CONSENT_KEY = 'regard-de-nature-cookie-consent';

type ConsentStatus = 'accepted' | 'rejected' | null;

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Petit délai pour ne pas afficher immédiatement
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleConsent = (status: ConsentStatus) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, status || 'rejected');
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-50 animate-in slide-in-from-bottom-4 duration-300">
      <div className="bg-card/95 backdrop-blur-sm border border-border rounded-xl shadow-lg p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-primary/10 rounded-lg shrink-0">
            <Cookie className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-foreground leading-relaxed">
              Nous utilisons des cookies pour améliorer votre expérience.{' '}
              <PrivacyPolicy triggerClassName="text-primary hover:underline inline cursor-pointer">
                <span className="text-primary hover:underline cursor-pointer">En savoir plus</span>
              </PrivacyPolicy>
            </p>
            <div className="flex gap-2 mt-3">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleConsent('rejected')}
                className="text-xs h-8"
              >
                Refuser
              </Button>
              <Button
                size="sm"
                onClick={() => handleConsent('accepted')}
                className="text-xs h-8"
              >
                Accepter
              </Button>
            </div>
          </div>
          <button
            onClick={() => handleConsent('rejected')}
            className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
            aria-label="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Hook pour vérifier le consentement
export const useCookieConsent = (): ConsentStatus => {
  const [consent, setConsent] = useState<ConsentStatus>(null);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    setConsent(stored as ConsentStatus);
  }, []);

  return consent;
};
